import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { Link } from "wouter";
import {
  Banknote,
  CalendarClock,
  Camera,
  CircleDollarSign,
  FileText,
  Flag,
  Mail,
  MapPin,
  MessageSquareText,
  Package,
  PenLine,
  Receipt,
  UserPlus,
  Wallet,
} from "lucide-react";
import { paths } from "@clevis/shared-types";
import { Card, CardTitle, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";

// Admin/owner job insights: the money summary (time, cost, billed, profit)
// and a visual activity timeline — every status move, text, document,
// payment, photo and reschedule as clickable, expandable events.

interface ActivityEvent {
  id: string;
  kind: string;
  title: string;
  detail: string;
  actorName: string;
  refType: "estimate" | "invoice" | "expense" | null;
  refId: string | null;
  amountCents: number | null;
  at: number;
}

interface Summary {
  minutesOnJob: number | null;
  live: boolean;
  laborCents: number | null;
  materialCents: number;
  materialSource?: "expenses" | "invoice_costs";
  /** A referral reward paid for winning this job (Clevis Relay). */
  referralCents?: number;
  billedCents: number;
  collectedCents: number;
  profitCents: number;
  marginPct: number | null;
}

const money = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const KIND_META: Record<string, { icon: typeof Flag; color: string }> = {
  created: { icon: Flag, color: "bg-steel" },
  status: { icon: Flag, color: "bg-brandorange" },
  schedule: { icon: CalendarClock, color: "bg-steel" },
  assign: { icon: UserPlus, color: "bg-steel" },
  arrival: { icon: MapPin, color: "bg-success" },
  text: { icon: MessageSquareText, color: "bg-brandorange" },
  email: { icon: Mail, color: "bg-brandorange" },
  estimate: { icon: FileText, color: "bg-ink" },
  invoice: { icon: Receipt, color: "bg-ink" },
  payment: { icon: Banknote, color: "bg-success" },
  photo: { icon: Camera, color: "bg-steel" },
  parts: { icon: Package, color: "bg-brandorange" },
  agreement: { icon: FileText, color: "bg-ink" },
  expense: { icon: Wallet, color: "bg-steel" },
  signoff: { icon: PenLine, color: "bg-success" },
  edit: { icon: PenLine, color: "bg-steel" },
};

export function JobInsights({ tenantId, jobId }: { tenantId: string; jobId: string }) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, paths.tenantJobActivity(tenantId, jobId)), orderBy("at", "desc"), limit(100)),
      (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ActivityEvent, "id">) }))),
    );
  }, [tenantId, jobId]);

  useEffect(() => {
    void call<Summary>("getJobSummary", { tenantId, jobId }, { mutation: false })
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [tenantId, jobId, events.length]);

  return (
    <Card>
      <CardTitle>Job insights</CardTitle>

      {summary ? (
        <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
          <div className="rounded-lg bg-cream px-3 py-2 text-center">
            <div className="text-base font-bold text-ink">
              {summary.minutesOnJob === null
                ? "—"
                : `${Math.floor(summary.minutesOnJob / 60)}h ${summary.minutesOnJob % 60}m`}
              {summary.live ? <span className="ml-1 text-[10px] text-brandorange">live</span> : null}
            </div>
            <div className="text-[11px] text-steel">Time on job</div>
          </div>
          <div className="rounded-lg bg-cream px-3 py-2 text-center">
            <div className="text-base font-bold text-ink">
              {money((summary.laborCents ?? 0) + summary.materialCents + (summary.referralCents ?? 0))}
            </div>
            <div className="text-[11px] text-steel">
              Cost · labor {summary.laborCents === null ? "n/a" : money(summary.laborCents)} +{" "}
              {summary.materialSource === "expenses" ? "expenses" : "mat"} {money(summary.materialCents)}
              {summary.referralCents ? ` + referral ${money(summary.referralCents)}` : ""}
            </div>
          </div>
          <div className="rounded-lg bg-cream px-3 py-2 text-center">
            <div className="text-base font-bold text-ink">{money(summary.billedCents)}</div>
            <div className="text-[11px] text-steel">Billed · {money(summary.collectedCents)} collected</div>
          </div>
          <div
            className={cn(
              "rounded-lg px-3 py-2 text-center",
              summary.billedCents === 0 ? "bg-cream" : summary.profitCents >= 0 ? "bg-success/15" : "bg-danger/10",
            )}
          >
            <div
              className={cn(
                "text-base font-bold",
                summary.billedCents === 0 ? "text-ink" : summary.profitCents >= 0 ? "text-success" : "text-danger",
              )}
            >
              {money(summary.profitCents)}
              {summary.marginPct !== null ? ` · ${summary.marginPct}%` : ""}
            </div>
            <div className="text-[11px] text-steel">Profit</div>
          </div>
        </div>
      ) : null}

      {/* The visual timeline */}
      <div className="relative ml-3 border-l-2 border-steel/20 pl-5">
        {events.map((e) => {
          const meta = KIND_META[e.kind] ?? KIND_META.created!;
          const Icon = meta.icon;
          const open = expanded === e.id;
          const hasMore = Boolean(e.detail || e.refType);
          return (
            <div key={e.id} className="relative pb-4 last:pb-0">
              <span
                className={cn(
                  "absolute -left-[31px] flex h-5 w-5 items-center justify-center rounded-full text-white",
                  meta.color,
                )}
              >
                <Icon size={11} />
              </span>
              <button
                className="block w-full text-left"
                onClick={() => hasMore && setExpanded(open ? null : e.id)}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className={cn("text-sm text-ink", hasMore && "cursor-pointer hover:text-brandorange")}>
                    {e.title}
                    {e.actorName ? <span className="text-xs text-steel"> · {e.actorName}</span> : null}
                    {e.amountCents ? (
                      <span className="ml-1 text-xs font-semibold text-steel">{money(e.amountCents)}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[11px] text-steel">
                    {new Date(e.at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </button>
              {open ? (
                <div className="mt-1 rounded-lg bg-cream px-3 py-2 text-xs text-ink">
                  {e.detail ? <p className="whitespace-pre-line">{e.detail}</p> : null}
                  {e.actorName ? <p className="text-steel">by {e.actorName}</p> : null}
                  {e.refType ? (
                    <Link
                      href={
                        e.refType === "expense"
                          ? `/expenses/${e.refId ?? ""}`
                          : e.refType === "estimate"
                            ? "/estimates"
                            : "/invoices"
                      }
                      className="text-brandorange-dark underline"
                    >
                      {e.refType === "expense" ? "Open expense →" : `Open ${e.refType}s →`}
                    </Link>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
        {events.length === 0 ? (
          <p className="pb-2 text-xs text-steel">
            The story starts now — new status changes, texts, documents and payments will appear here.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
